/**
 * Biblioteca de componente a dashboard-ului.
 *
 * Aspectul este complet propriu (fără temă de admin „default"), construit peste Tailwind și
 * tokenii din docs/UI_SYSTEM.md. Componentele sunt mici, accesibile (roluri ARIA, focus vizibil,
 * navigare cu tastatura) și nu ascund stări: fiecare listă are loading / empty / error.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

/* --------------------------------------------------------------------- utilitare */

export function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

/* ------------------------------------------------------------------------ Button */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-700 shadow-[var(--shadow-card)]',
  secondary: 'bg-surface text-ink-950 border border-ink-200 hover:bg-surface-3 active:bg-ink-100',
  ghost: 'bg-transparent text-ink-600 hover:bg-surface-3 hover:text-ink-950',
  danger: 'bg-surface text-danger border border-danger/30 hover:bg-danger-bg',
  link: 'bg-transparent text-brand-600 hover:text-brand-700 underline underline-offset-4 px-0',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-body-s',
  md: 'h-11 px-4 text-body-s font-medium',
  lg: 'h-12 px-6 text-body',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'transition-ui inline-flex items-center justify-center gap-2 rounded-[var(--radius-md)]',
        'font-medium whitespace-nowrap select-none',
        'disabled:cursor-not-allowed disabled:opacity-55',
        BUTTON_SIZES[size],
        BUTTON_VARIANTS[variant],
        className,
      )}
    >
      {loading ? <Spinner size={16} /> : icon}
      {children}
    </button>
  );
}

/* ----------------------------------------------------------------------- Inputs */

export interface FieldProps {
  label: string;
  hint?: string;
  errors?: string[];
  required?: boolean;
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode;
}

export function Field({ label, hint, errors, required, children }: FieldProps) {
  const id = useId();
  const describedBy = [hint ? `${id}-hint` : null, errors?.length ? `${id}-error` : null].filter(Boolean).join(' ');
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-body-s font-medium text-ink-800">
        {label}
        {required ? <span className="ml-0.5 text-danger">*</span> : null}
      </label>
      {children({
        id,
        ...(errors?.length ? { 'aria-invalid': true } : {}),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      })}
      {hint ? (
        <p id={`${id}-hint`} className="text-caption text-ink-400">
          {hint}
        </p>
      ) : null}
      {errors?.length ? (
        <p id={`${id}-error`} className="text-caption text-danger">
          {errors[0]}
        </p>
      ) : null}
    </div>
  );
}

const INPUT_BASE =
  'transition-ui w-full rounded-[var(--radius-md)] border border-ink-200 bg-surface px-3.5 text-body ' +
  'placeholder:text-ink-400 hover:border-ink-400 focus:border-brand-600 focus:outline-none ' +
  'disabled:bg-surface-3 disabled:text-ink-400 aria-[invalid=true]:border-danger';

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cx(INPUT_BASE, 'h-11', className)} />;
}

export function Textarea({ className, rows = 4, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} rows={rows} className={cx(INPUT_BASE, 'py-2.5 leading-6', className)} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...rest}
      className={cx(INPUT_BASE, 'h-11 pr-9 appearance-none bg-no-repeat', className)}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%234A5568' stroke-width='1.6' viewBox='0 0 24 24'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        backgroundPosition: 'right 12px center',
      }}
    >
      {children}
    </select>
  );
}

const CLOCK_HOURS = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0'));
const CLOCK_MINUTES = Array.from({ length: 60 }, (_, minute) => String(minute).padStart(2, '0'));

/** Oră 00:00–23:59, fără AM/PM. Valoarea rămâne `HH:mm` sau șir gol. */
export function Clock24({
  value,
  onChange,
  allowEmpty = false,
  disabled = false,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  allowEmpty?: boolean;
  disabled?: boolean;
  id?: string;
}) {
  const [rawHour = '', rawMinute = ''] = value.split(':');
  const hour = rawHour.padStart(2, '0');
  const minute = rawMinute.padStart(2, '0');
  const hourValue = value ? hour.slice(-2) : '';
  const minuteValue = value ? minute.slice(-2) : '';

  function emit(nextHour: string, nextMinute: string) {
    if (!nextHour && !nextMinute) {
      onChange('');
      return;
    }
    onChange(`${nextHour || '00'}:${nextMinute || '00'}`);
  }

  return (
    <span className="flex items-center gap-1.5">
      <select
        id={id}
        aria-label="Ora, 00–23"
        disabled={disabled}
        value={hourValue}
        onChange={(event) => emit(event.target.value, minuteValue)}
        className={cx(INPUT_BASE, 'h-11 w-[4.75rem] px-2 font-mono nums')}
      >
        {allowEmpty ? <option value="">—</option> : null}
        {CLOCK_HOURS.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
      <span className="text-body text-ink-500" aria-hidden>
        :
      </span>
      <select
        aria-label="Minutul, 00–59"
        disabled={disabled}
        value={minuteValue}
        onChange={(event) => emit(hourValue, event.target.value)}
        className={cx(INPUT_BASE, 'h-11 w-[4.75rem] px-2 font-mono nums')}
      >
        {allowEmpty ? <option value="">—</option> : null}
        {CLOCK_MINUTES.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
    </span>
  );
}

/** Dată plus oră 00:00–23:59 în formatul `YYYY-MM-DDTHH:mm`. */
export function DateTime24({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  id?: string;
}) {
  const date = value.slice(0, 10);
  const time = value.length >= 16 ? value.slice(11, 16) : '';

  return (
    <span className="flex flex-wrap items-center gap-2">
      <input
        id={id}
        type="date"
        lang="ro-RO"
        value={date}
        onChange={(event) => {
          const nextDate = event.target.value;
          if (!nextDate) {
            onChange('');
            return;
          }
          onChange(`${nextDate}T${time || '00:00'}`);
        }}
        className={cx(INPUT_BASE, 'h-11 w-[11.5rem]')}
      />
      <Clock24
        value={date ? time || '00:00' : ''}
        allowEmpty={!date}
        disabled={!date}
        onChange={(nextTime) => {
          if (!date) return;
          onChange(nextTime ? `${date}T${nextTime}` : `${date}T00:00`);
        }}
      />
    </span>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-6">
      <span className="flex flex-col gap-0.5">
        <span className="text-body-s font-medium text-ink-800">{label}</span>
        {description ? <span className="text-caption text-ink-400">{description}</span> : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={cx(
          'transition-ui relative mt-0.5 h-6 w-11 shrink-0 rounded-full',
          checked ? 'bg-brand-600' : 'bg-ink-200',
        )}
      >
        <span
          className={cx(
            'transition-ui absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm',
            checked && 'translate-x-5',
          )}
        />
      </button>
    </label>
  );
}

/* ------------------------------------------------------------------------- Card */

export function Card({
  children,
  className,
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'article' | 'li';
}) {
  /*
   * `min-w-0` este deliberat implicit aici.
   *
   * Defect real, măsurat pe trei pagini deodată (Produse, Meniuri, „Astăzi"): un card folosit ca
   * element de grilă sau de flex NU coboară sub lățimea minimă a conținutului lui (`min-width: auto`
   * este valoarea implicită în flex/grid). Dacă în card există un rând care nu se poate rupe — preț și
   * butoane, „Ultima publicare: … · Republică", etichete — grila se lărgește peste ecran, iar pagina
   * întreagă capătă derulare laterală (măsurat: 451 px, 755 px și 480 px pe un ecran de 390 px).
   * `min-w-0` nu schimbă nimic la lățime normală (blocurile își iau lățimea din container); doar
   * permite conținutului să se replieze în loc să împingă pagina.
   */
  return (
    <Tag
      className={cx(
        'min-w-0 rounded-[var(--radius-lg)] border border-ink-100 bg-surface p-5 shadow-[var(--shadow-card)]',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('mb-4 flex flex-wrap items-start justify-between gap-4', className)}>
      <div className="flex min-w-0 flex-col gap-1">
        <h3 className="font-display text-title text-ink-950">{title}</h3>
        {subtitle ? <p className="text-body-s text-ink-600">{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex flex-col gap-1">
        <h1 className="font-display text-display-m text-ink-950">{title}</h1>
        {subtitle ? <p className="text-body text-ink-600">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/* ------------------------------------------------------------------ KPI & badge */

export function KpiCard({
  label,
  value,
  hint,
  trend,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  trend?: { value: string; positive?: boolean };
}) {
  return (
    <Card className="flex flex-col gap-2">
      <span className="text-body-s text-ink-600">{label}</span>
      <span className="nums font-display text-display-m leading-none text-ink-950">{value}</span>
      <span className="flex items-center gap-2 text-caption text-ink-400">
        {trend ? (
          <span className={cx('font-medium', trend.positive ? 'text-success' : 'text-danger')}>{trend.value}</span>
        ) : null}
        {hint}
      </span>
    </Card>
  );
}

type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-3 text-ink-600',
  brand: 'bg-brand-100 text-brand-700',
  success: 'bg-success-bg text-success',
  warning: 'bg-warning-bg text-warning',
  danger: 'bg-danger-bg text-danger',
  info: 'bg-info-bg text-info',
  accent: 'bg-accent-100 text-accent-600',
};

export function Badge({ tone = 'neutral', children, className }: { tone?: BadgeTone; children: ReactNode; className?: string }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-caption font-medium',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function StatusDot({ tone = 'neutral', pulse = false }: { tone?: 'success' | 'warning' | 'danger' | 'neutral'; pulse?: boolean }) {
  const color = {
    success: 'bg-success',
    warning: 'bg-accent-500',
    danger: 'bg-danger',
    neutral: 'bg-ink-400',
  }[tone];
  return (
    <span className="relative inline-flex h-2 w-2">
      {pulse ? <span className={cx('absolute inline-flex h-full w-full animate-ping rounded-full opacity-60', color)} /> : null}
      <span className={cx('relative inline-flex h-2 w-2 rounded-full', color)} />
    </span>
  );
}

/* --------------------------------------------------------------------- feedback */

export function Spinner({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="animate-spin">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" fill="none" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    </svg>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('skeleton h-4 w-full', className)} />;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[var(--radius-lg)] border border-dashed border-ink-200 bg-surface px-6 py-12 text-center">
      {icon ? <div className="text-ink-400">{icon}</div> : null}
      <div className="flex flex-col gap-1">
        <p className="font-display text-title text-ink-950">{title}</p>
        {description ? <p className="mx-auto max-w-md text-body-s text-ink-600">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function InlineAlert({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success';
  title?: string;
  children: ReactNode;
}) {
  const styles = {
    info: 'bg-info-bg/60 text-info',
    warning: 'bg-warning-bg/70 text-warning',
    danger: 'bg-danger-bg/70 text-danger',
    success: 'bg-success-bg/70 text-success',
  }[tone];
  const role = tone === 'danger' ? 'alert' : 'status';
  return (
    <div role={role} className={cx('flex flex-col gap-1 rounded-[var(--radius-md)] px-4 py-3 text-body-s', styles)}>
      {title ? <span className="font-medium">{title}</span> : null}
      <span className="text-ink-800">{children}</span>
    </div>
  );
}

/* ----------------------------------------------------------------------- Toasts */

export interface Toast {
  id: number;
  tone: 'success' | 'error' | 'info';
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  push: (toast: Omit<Toast, 'id'>) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const remove = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      counter.current += 1;
      const id = counter.current;
      setToasts((current) => [...current.slice(-2), { ...toast, id }]);
      window.setTimeout(() => remove(id), 5000);
    },
    [remove],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      push,
      success: (title, description) => push({ tone: 'success', title, ...(description ? { description } : {}) }),
      error: (title, description) => push({ tone: 'error', title, ...(description ? { description } : {}) }),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:items-end"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="page-enter pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-[var(--radius-md)] border border-ink-100 bg-surface p-4 shadow-[var(--shadow-elevated)]"
          >
            <span
              className={cx(
                'mt-0.5 h-2 w-2 shrink-0 rounded-full',
                toast.tone === 'success' ? 'bg-success' : toast.tone === 'error' ? 'bg-danger' : 'bg-info',
              )}
            />
            <div className="flex flex-1 flex-col gap-0.5">
              <p className="text-body-s font-medium text-ink-950">{toast.title}</p>
              {toast.description ? <p className="text-caption text-ink-600">{toast.description}</p> : null}
              {toast.action ? (
                <button
                  type="button"
                  onClick={() => {
                    toast.action?.onClick();
                    remove(toast.id);
                  }}
                  className="transition-ui mt-1 self-start text-caption font-medium text-brand-600 hover:text-brand-700"
                >
                  {toast.action.label}
                </button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => remove(toast.id)}
              aria-label="Închide notificarea"
              className="transition-ui -mt-1 -mr-1 rounded p-1 text-ink-400 hover:bg-surface-3 hover:text-ink-800"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast trebuie folosit în interiorul <ToastProvider>');
  return ctx;
}

/* ------------------------------------------------------------------------ Modal */

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const body = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  /**
   * La fiecare deschidere, conținutul pornește de sus.
   *
   * Fără asta, un panou deschis, derulat până la capăt și închis s-ar redeschide derulat — iar omul
   * ar crede că lipsește partea de sus (titlul și primele câmpuri).
   */
  useEffect(() => {
    if (open) body.current?.scrollTo({ top: 0 });
  }, [open]);

  if (!open) return null;
  const width = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' }[size];

  /*
   * Panoul se încadrează în ecran și derulează în interior.
   *
   * Defect real, raportat din folosire: modalurile mari (de exemplu „Setări comenzi", după ce s-au
   * adăugat setările de notificare) **depășeau ecranul și nu puteau fi derulate**. Cauza: acest
   * component bloca derularea paginii din spate (`body { overflow: hidden }`, corect — altfel se
   * derula dashboard-ul sub panou), dar panoul în sine nu avea nici înălțime maximă, nici derulare.
   * Conținutul care nu încăpea pur și simplu ieșea din ecran: butonul „Salvează" și câmpurile de jos
   * erau imposibil de ajuns, iar pe telefon erau ascunse și de bara browserului.
   *
   * Structura de acum: antetul și subsolul stau pe loc, doar zona din mijloc derulează. Este exact
   * tiparul care trebuie respectat pentru orice conținut lung — inclusiv pe ecrane mici, unde
   * înălțimea reală se schimbă când apare bara de adrese (`dvh`, cu rezervă `vh` pentru browsere
   * mai vechi).
   */
  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center overflow-y-auto overscroll-contain bg-ink-950/45 sm:items-center sm:p-6"
      role="presentation"
    >
      <button type="button" aria-label="Închide" className="fixed inset-0 cursor-default" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cx(
          'page-enter relative flex w-full max-h-[calc(100vh-1rem)] flex-col rounded-t-[var(--radius-xl)] bg-surface shadow-[var(--shadow-elevated)]',
          'sm:max-h-[calc(100vh-3rem)] sm:rounded-[var(--radius-lg)]',
          'supports-[height:100dvh]:max-h-[calc(100dvh-1rem)] supports-[height:100dvh]:sm:max-h-[calc(100dvh-3rem)]',
          width,
        )}
      >
        <div className="flex shrink-0 flex-col gap-1 border-b border-ink-100 px-6 pt-6 pb-4">
          <h2 className="font-display text-title text-ink-950">{title}</h2>
          {description ? <p className="text-body-s text-ink-600">{description}</p> : null}
        </div>

        {/* `min-h-0` este obligatoriu: fără el, un copil flex nu coboară sub înălțimea conținutului
            și derularea nu se activează niciodată (capcana clasică de flexbox). */}
        <div ref={body} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
          {children}
        </div>

        {footer ? (
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-ink-100 px-6 pt-4 pb-6">{footer}</div>
        ) : null}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirmă',
  tone = 'danger',
  loading = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  tone?: 'danger' | 'primary';
  loading?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Anulează
          </Button>
          <Button
            onClick={onConfirm}
            loading={loading}
            className={tone === 'danger' ? 'bg-danger text-white hover:bg-danger/90' : undefined}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-body-s text-ink-600">{description}</p>
    </Modal>
  );
}

/* ---------------------------------------------------------------------- Avatar */

export function Avatar({ name, size = 36, className }: { name: string; size?: number; className?: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-full bg-brand-100 font-medium text-brand-700',
        className,
      )}
    >
      {initials || '•'}
    </span>
  );
}

/* ------------------------------------------------------------------- iconițe minime */

export function Icon({ name, size = 18, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {ICONS[name]}
    </svg>
  );
}

export type IconName =
  | 'home'
  | 'menu'
  | 'qr'
  | 'display'
  | 'chart'
  | 'tag'
  | 'store'
  | 'pin'
  | 'users'
  | 'settings'
  | 'bell'
  | 'logout'
  | 'plus'
  | 'check'
  | 'chevron-down'
  | 'chevron-right'
  | 'search'
  | 'external'
  | 'shield'
  | 'sparkles'
  | 'close'
  | 'copy'
  | 'grid'
  | 'clock'
  | 'receipt'
  | 'globe'
  | 'image'
  | 'upload'
  | 'download'
  | 'trending'
  | 'alert'
  | 'card';

const ICONS: Record<IconName, ReactNode> = {
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="m4 18 5-4.5 4 3.5 3-2.5 4 3.5" />
    </>
  ),
  upload: (
    <>
      <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" />
      <path d="M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16" />
    </>
  ),
  // Descărcare: săgeata în jos peste „raft", complementul iconiței de încărcare.
  download: (
    <>
      <path d="M12 4v12m0 0 4.5-4.5M12 16l-4.5-4.5" />
      <path d="M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16" />
    </>
  ),
  // Tendință: folosită pentru evoluții (statistici, abonament).
  trending: (
    <>
      <path d="M3 17l5.5-5.5 4 4L21 7" />
      <path d="M15.5 7H21v5.5" />
    </>
  ),
  // Card bancar: abonament și facturare.
  card: (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="M2.5 10h19M6 15h4" />
    </>
  ),
  // Alertă: semnale de verificat (promoții expirate, ecrane offline).
  alert: (
    <>
      <path d="M12 3.5 21 19H3z" />
      <path d="M12 9.5v4M12 16.5v.01" />
    </>
  ),
  home: <path d="M3 10.5 12 3l9 7.5M5.5 9.5V20h13V9.5" />,
  menu: <path d="M4 6h16M4 12h16M4 18h10" />,
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <path d="M14 14h3v3h-3zM20 14v.01M20 20v.01M17 20v.01" />
    </>
  ),
  display: (
    <>
      <rect x="2.5" y="4" width="19" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </>
  ),
  chart: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  tag: (
    <>
      <path d="M20.5 13.5 13 21a2 2 0 0 1-2.8 0L3 13.8V4h9.8l7.7 7.7a1.3 1.3 0 0 1 0 1.8Z" />
      <path d="M7.5 8h.01" />
    </>
  ),
  store: <path d="M3 9.5 4.5 4h15L21 9.5M4.5 9.5V20h15V9.5M4.5 9.5h15" />,
  pin: (
    <>
      <path d="M12 21s7-5.3 7-11a7 7 0 1 0-14 0c0 5.7 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 5.2a3.2 3.2 0 0 1 0 5.6M18 20a6 6 0 0 0-2-4.5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2 2 2 0 1 1-4 0 1.7 1.7 0 0 0-2.9-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 3 15a2 2 0 1 1 0-4 1.7 1.7 0 0 0 1.5-2.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 10 3a2 2 0 1 1 4 0 1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1A1.7 1.7 0 0 0 21 11a2 2 0 1 1 0 4Z" />
    </>
  ),
  bell: <path d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 13 6 9ZM10 18a2 2 0 0 0 4 0" />,
  logout: <path d="M15 4h3.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H15M10 8 6 12l4 4M6 12h9" />,
  plus: <path d="M12 5v14M5 12h14" />,
  check: <path d="m4.5 12.5 5 5 10-11" />,
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  'chevron-right': <path d="m9 6 6 6-6 6" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  external: <path d="M14 4h6v6M20 4l-8.5 8.5M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />,
  shield: <path d="M12 3l7.5 3v6c0 4.5-3.2 8-7.5 9.5C7.7 20 4.5 16.5 4.5 12V6L12 3Z" />,
  sparkles: <path d="M12 3.5 13.6 9 19 10.5 13.6 12 12 17.5 10.4 12 5 10.5 10.4 9 12 3.5ZM19 16l.7 2.3L22 19l-2.3.7L19 22l-.7-2.3L16 19l2.3-.7L19 16Z" />,
  close: <path d="M18 6 6 18M6 6l12 12" />,
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M15 5.5A1.5 1.5 0 0 0 13.5 4H5.5A1.5 1.5 0 0 0 4 5.5v8A1.5 1.5 0 0 0 5.5 15" />
    </>
  ),
  grid: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="2" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="2" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="2" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="2" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  // Bon de comandă: marginea de jos este zimțată, ca la o hârtie ruptă din imprimantă.
  receipt: (
    <>
      <path d="M5 3.5h14v17l-2.3-1.6-2.3 1.6-2.4-1.6-2.3 1.6L7.3 18.9 5 20.5Z" />
      <path d="M8.5 8h7M8.5 11.5h7M8.5 15h4" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.5 2.4 3.6 5.4 3.6 8.5S14.5 18.1 12 20.5c-2.5-2.4-3.6-5.4-3.6-8.5S9.5 5.9 12 3.5Z" />
    </>
  ),
};
